/* ===========================================================================
 *  Meekijken: iemand deelt zijn scherm, een ontwikkelaar kijkt mee
 *
 *  Casper, 23 okt 2026: "een soort teamviewer in IT, zodat de andere
 *  gebruiker een melding krijgt om te accepteren of niet". Geen overnemen:
 *  alleen beeld, en alleen na ja.
 *
 *  Hoe het loopt
 *  -------------
 *
 *    kijker (ontwikkelaar)              deler (de gebruiker)
 *    meekijk-vraag  ------------------> ziet het verzoek (poller, 5 s)
 *                                       Accepteren: EERST getDisplayMedia
 *                                       (in de klik zelf, anders weigert de
 *                                       browser), DAN meekijk-antwoord ja
 *                                       aanbod maken, wachten tot ALLE
 *                                       kandidaten er zijn (iceGatheringState
 *                                       'complete'), dan meekijk-signaal
 *    meekijk-stand (pollen) <---------- het hele aanbod in één keer
 *    antwoord maken, wachten op
 *    'complete', meekijk-signaal -----> meekijk-stand (pollen) -> antwoord
 *    beeld                              "Je deelt je scherm met ..."
 *
 *  Niet-trickle: geen losse kandidaten heen en weer, maar één aanbod en één
 *  antwoord met alles erin. Dat kost een seconde bij het opzetten en scheelt
 *  een hele tabel met kandidaten en een poller die die afloopt.
 *
 *  Alleen STUN, geen TURN: achter een strenge bedrijfsfirewall (symmetrische
 *  NAT aan beide kanten) komt er dan geen beeld. Dat meldt het scherm; een
 *  TURN-server is een volgende stap als het in de praktijk nodig blijkt.
 *
 *  Waarom dit één los bestand is zonder imports: zodat het te lezen is
 *  zonder React eromheen, en zodat dev/meekijken-nakijken.ts het met
 *  nep-verbindingen kan afspelen (node draait .ts zonder bouwstap, maar
 *  dan zonder Vite-paden). De server en de browser komen er als
 *  parameters in.
 * =========================================================================== */

import type { MeekijkStand } from '../../gedeeld/types'

export const STUN: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] },
]

/** Hoe vaak de stand gevraagd wordt terwijl er iets loopt. */
export const STAND_MS = 1500

/** Hoe lang hooguit wachten tot de kandidaten er allemaal zijn. */
export const VERZAMEL_MS = 10_000

/** Wat er bij de server te vragen is (api.meekijk* in api.ts). */
export interface MeekijkServer {
  stand(id: string): Promise<MeekijkStand>
  signaal(id: string, sdp: string): Promise<unknown>
  antwoord(id: string, akkoord: boolean): Promise<unknown>
  stop(id: string): Promise<unknown>
}

/** Wat er van de browser nodig is. In de toets nep. */
export interface MeekijkBrowser {
  verbinding(instelling: RTCConfiguration): RTCPeerConnection
  schermVragen(): Promise<MediaStream>
}

export const echteBrowser: MeekijkBrowser = {
  verbinding: (instelling) => new RTCPeerConnection(instelling),
  schermVragen: () => navigator.mediaDevices.getDisplayMedia({ video: true, audio: false }),
}

const KLAAR: MeekijkStand['status'][] = ['gestopt', 'verlopen', 'geweigerd']

/* ------------------------------------------------------------------ *
 *  Wachten tot alle kandidaten er zijn
 *
 *  Pas daarna staat in localDescription een aanbod waar de ander iets mee
 *  kan. Geeft hij na tien seconden nog geen 'complete' (een netwerkkaart
 *  die blijft zoeken), dan gaat wat er is: meestal zit de bruikbare
 *  kandidaat er dan allang in.
 * ------------------------------------------------------------------ */

export function wachtOpKandidaten(pc: RTCPeerConnection, maxMs = VERZAMEL_MS): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((klaar) => {
    let klok: ReturnType<typeof setTimeout> | undefined
    const af = () => {
      pc.removeEventListener('icegatheringstatechange', kijk)
      if (klok !== undefined) clearTimeout(klok)
      klaar()
    }
    const kijk = () => { if (pc.iceGatheringState === 'complete') af() }
    pc.addEventListener('icegatheringstatechange', kijk)
    klok = setTimeout(af, maxMs)
  })
}

/* ------------------------------------------------------------------ *
 *  De deler: wiens scherm het is
 * ------------------------------------------------------------------ */

export type DelerFase = 'kiezen' | 'verbinden' | 'wachten' | 'delen' | 'gestopt' | 'mislukt'

/** Hoe het delen ophield. 'ander' = de server zegt dat het voorbij is; dan niet nog eens stoppen. */
export type StopHoe = 'zelf' | 'browser' | 'weg' | 'ander'

export interface Deler {
  /** Accepteren. Met een stroom als het scherm al gekozen is (in de melding). */
  start(stroom?: MediaStream): Promise<boolean>
  stop(hoe: StopHoe): Promise<void>
  fase(): DelerFase
}

export function maakDeler(o: {
  id: string
  server: MeekijkServer
  browser?: MeekijkBrowser
  opFase: (f: DelerFase, tekst?: string) => void
  standMs?: number
  verzamelMs?: number
}): Deler {
  const browser = o.browser ?? echteBrowser
  let fase: DelerFase = 'kiezen'
  let stroom: MediaStream | null = null
  let pc: RTCPeerConnection | null = null
  let klok: ReturnType<typeof setTimeout> | undefined
  let gestopt = false
  let antwoordGezet = false

  const zet = (f: DelerFase, tekst?: string) => { fase = f; o.opFase(f, tekst) }

  function opruimen() {
    if (klok !== undefined) clearTimeout(klok)
    klok = undefined
    for (const t of stroom?.getTracks() ?? []) {
      t.onended = null
      t.stop()
    }
    stroom = null
    if (pc) {
      pc.onconnectionstatechange = null
      pc.close()
    }
    pc = null
  }

  async function stop(hoe: StopHoe) {
    if (gestopt) return
    gestopt = true
    opruimen()
    zet('gestopt', hoe === 'browser' ? 'Het delen is gestopt vanuit de browser.' : undefined)
    if (hoe !== 'ander') {
      try { await o.server.stop(o.id) } catch { /* de wekker zet hem anders op verlopen */ }
    }
  }

  async function ronde() {
    klok = undefined
    if (gestopt || !pc) return
    try {
      const s = await o.server.stand(o.id)
      if (gestopt) return
      if (KLAAR.includes(s.status)) { await stop('ander'); return }
      if (s.antwoord && !antwoordGezet) {
        antwoordGezet = true
        await pc.setRemoteDescription({ type: 'answer', sdp: s.antwoord })
        if (fase === 'wachten') zet('verbinden')
      }
    } catch { /* even geen verbinding met de server: volgende ronde */ }
    /* Loopt het eenmaal, dan is de vraag alleen nog "is het gestopt?". */
    if (!gestopt) klok = setTimeout(() => void ronde(), (o.standMs ?? STAND_MS) * (antwoordGezet ? 2 : 1))
  }

  async function start(gekozen?: MediaStream): Promise<boolean> {
    if (gestopt || pc) return false
    zet('kiezen')
    try {
      stroom = gekozen ?? await browser.schermVragen()
    } catch {
      /* Weggeklikt of geen toestemming in de browser. Dan NIET akkoord: de
         vraag blijft staan en je kunt het opnieuw proberen of weigeren. */
      zet('mislukt', 'Je hebt niets gekozen om te delen. Er is nog niets gedeeld.')
      return false
    }
    if (gestopt) { opruimen(); return false }
    for (const t of stroom.getVideoTracks()) t.onended = () => { void stop('browser') }

    try {
      zet('verbinden')
      await o.server.antwoord(o.id, true)
      if (gestopt) return false
      pc = browser.verbinding({ iceServers: STUN })
      for (const t of stroom.getTracks()) pc.addTrack(t, stroom)
      pc.onconnectionstatechange = () => {
        if (!pc || gestopt) return
        if (pc.connectionState === 'connected') zet('delen')
        if (pc.connectionState === 'failed') {
          zet('mislukt', 'Er kwam geen verbinding tot stand. Misschien houdt een firewall het tegen.')
        }
      }
      await pc.setLocalDescription(await pc.createOffer())
      await wachtOpKandidaten(pc, o.verzamelMs)
      if (gestopt || !pc) return false
      const sdp = pc.localDescription?.sdp
      if (!sdp) throw new Error('Er is geen aanbod gemaakt.')
      await o.server.signaal(o.id, sdp)
      if (gestopt) return false
      zet('wachten')
      klok = setTimeout(() => void ronde(), o.standMs ?? STAND_MS)
      return true
    } catch (e) {
      const tekst = e instanceof Error ? e.message : String(e)
      if (!gestopt) {
        gestopt = true
        opruimen()
        zet('mislukt', tekst)
        try { await o.server.stop(o.id) } catch { /* zie stop() */ }
      }
      return false
    }
  }

  return { start, stop, fase: () => fase }
}

/* ------------------------------------------------------------------ *
 *  De kijker: de ontwikkelaar die het vroeg
 * ------------------------------------------------------------------ */

export interface Kijker {
  start(): void
  stop(hoe: StopHoe): Promise<void>
}

export function maakKijker(o: {
  id: string
  server: MeekijkServer
  browser?: MeekijkBrowser
  opStand: (s: MeekijkStand) => void
  opBeeld: (s: MediaStream | null) => void
  opVerbinding?: (staat: RTCPeerConnectionState) => void
  opFout?: (tekst: string) => void
  standMs?: number
  verzamelMs?: number
}): Kijker {
  const browser = o.browser ?? echteBrowser
  let pc: RTCPeerConnection | null = null
  let klok: ReturnType<typeof setTimeout> | undefined
  let gestopt = false

  function opruimen() {
    if (klok !== undefined) clearTimeout(klok)
    klok = undefined
    if (pc) {
      pc.ontrack = null
      pc.onconnectionstatechange = null
      pc.close()
    }
    pc = null
    o.opBeeld(null)
  }

  async function stop(hoe: StopHoe) {
    if (gestopt) return
    gestopt = true
    opruimen()
    if (hoe !== 'ander') {
      try { await o.server.stop(o.id) } catch { /* de wekker ruimt op */ }
    }
  }

  async function verbind(aanbod: string) {
    const deze = browser.verbinding({ iceServers: STUN })
    pc = deze
    deze.ontrack = (e) => { const s = e.streams[0]; if (s && !gestopt) o.opBeeld(s) }
    deze.onconnectionstatechange = () => { if (!gestopt) o.opVerbinding?.(deze.connectionState) }
    await deze.setRemoteDescription({ type: 'offer', sdp: aanbod })
    await deze.setLocalDescription(await deze.createAnswer())
    await wachtOpKandidaten(deze, o.verzamelMs)
    if (gestopt || pc !== deze) return
    const sdp = deze.localDescription?.sdp
    if (!sdp) throw new Error('Er is geen antwoord gemaakt.')
    await o.server.signaal(o.id, sdp)
  }

  async function ronde() {
    klok = undefined
    if (gestopt) return
    try {
      const s = await o.server.stand(o.id)
      if (gestopt) return
      o.opStand(s)
      if (KLAAR.includes(s.status)) { await stop('ander'); return }
      if (s.aanbod && !pc) await verbind(s.aanbod)
    } catch (e) {
      o.opFout?.(e instanceof Error ? e.message : String(e))
    }
    if (!gestopt) klok = setTimeout(() => void ronde(), (o.standMs ?? STAND_MS) * (pc ? 2 : 1))
  }

  let begonnen = false
  return {
    start: () => { if (!gestopt && !begonnen) { begonnen = true; void ronde() } },
    stop,
  }
}

/* ------------------------------------------------------------------ *
 *  De stille poller: is er een verzoek voor mij?
 *
 *  Elke vijf seconden, en ALLEEN als het tabblad zichtbaar is. Een tabblad
 *  op de achtergrond vraagt niets (twintig open tabbladen zijn anders
 *  twintig vragen per vijf seconden aan de functie it); komt het weer in
 *  beeld, dan meteen een vraag. Een vraag die nog loopt wordt niet
 *  ingehaald door de volgende.
 * ------------------------------------------------------------------ */

export const POLL_MS = 5000

export interface Zichtbaarheid {
  readonly visibilityState: string
  addEventListener(soort: 'visibilitychange', f: () => void): void
  removeEventListener(soort: 'visibilitychange', f: () => void): void
}

/** Geeft een functie terug die hem stopt. */
export function startPoller(vraag: () => Promise<unknown>, o: { doc: Zichtbaarheid; elke?: number }): () => void {
  let bezig = false
  let weg = false
  const tik = () => {
    if (weg || bezig || o.doc.visibilityState !== 'visible') return
    bezig = true
    vraag().catch(() => { /* stil: de volgende ronde probeert het opnieuw */ }).finally(() => { bezig = false })
  }
  const zicht = () => { if (o.doc.visibilityState === 'visible') tik() }
  o.doc.addEventListener('visibilitychange', zicht)
  const klok = setInterval(tik, o.elke ?? POLL_MS)
  tik()
  return () => {
    weg = true
    clearInterval(klok)
    o.doc.removeEventListener('visibilitychange', zicht)
  }
}

/* ------------------------------------------------------------------ *
 *  Wie ziet de knoppen "Bekijk als" en "Meekijken"
 *
 *  Alleen een ontwikkelaar, niet bij zichzelf en niet bij iemand die uit
 *  dienst is. De functie it kijkt dit (en meer: tw10.gebruiken, één
 *  sessie tegelijk) bij elke actie opnieuw; dit is alleen wat er te zien is.
 * ------------------------------------------------------------------ */

export function magKijkKnoppen(o: { ontwikkelaar: boolean; ikId: string | null | undefined; persoonId: string; actief: boolean }): boolean {
  return o.ontwikkelaar === true && !!o.ikId && o.ikId !== o.persoonId && o.actief === true
}

/** Een reden moet iets zeggen: tien tekens, net als de functie it. */
export const MIN_REDEN = 10
export const redenGoed = (r: string) => r.trim().length >= MIN_REDEN
